import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { reduxForm } from 'redux-form'
import { compose } from 'redux'
import { Collapse } from 'react-collapse'
import cx from 'classnames'
import { useForm } from 'react-hook-form'

import CtaButton from '@cred/react-cta'
import { Testable } from '@cred/react-testable'
import { withSnowplowContexts } from '@cred/react-analytics/SnowplowContextManager'
import Dropdown from '@cred/react-basic-form-fields/hookForm/Dropdown'
import Checkbox from '@cred/react-basic-form-fields/hookForm/Checkbox'
import Input from '@cred/react-basic-form-fields/hookForm/Input'
import Money from '@cred/react-basic-form-fields/hookForm/Money'

import { validateZipWithPrefill, handleChangeZip } from 'common/validations/zip-code'
import HelpIconWithModal from 'components/help-icon-with-modal'
import isFeatureEnabled from 'utils/featureToggle/isFeatureEnabled'
import { LOAN_TYPE_FILTER } from 'utils/featureToggle/const'
import renderDropdownOptions from 'utils/renderDropdownOptions'

import {
  propertyTypes,
  occupancyTypes,
  ficoTypes,
  termsHeaderLabelShortVersion,
  filterTypeList,
  verticalOptions,
} from '../common/options'
import {
  PURCHASE_TYPE,
  LOAN_TERM_MODAL_NAME,
  LOAN_TYPE_MODAL_NAME,
  CONVENTIONAL_LOAN_TYPE_FILTER_VALUE,
  FHA_LOAN_TYPE_FILTER_VALUE,
} from '../common/constants'
import LoanTermModal from './loan-term-modal/LoanTermModal'
import LoanTypeModal from './loan-term-modal/LoanTypeModal'
import { loanTypesList } from '../common/config'
import styles from './themes/default/styles.module.scss'

const giveAdjacentValue = (key, obj) => {
  const oppositeKey =
    key === CONVENTIONAL_LOAN_TYPE_FILTER_VALUE
      ? FHA_LOAN_TYPE_FILTER_VALUE
      : CONVENTIONAL_LOAN_TYPE_FILTER_VALUE
  return obj[oppositeKey]
}

const isOtherPropertiesFalsy = (key, obj) => {
  if (Object.keys(obj).length === 1) return true
  // eslint-disable-next-line no-restricted-syntax,no-unused-vars
  for (const property in obj) {
    if (property !== key && obj[property]) {
      return false
    }
  }
  return true
}

const FilterFormCombinedNoHOC = (props) => {
  const {
    analyticsContexts = [],
    loadingProducts,
    invalid,
    onSubmit,
    isCashOutError,
    isValidationsError,
    isOpen,
    editButton,
    filterText,
    sortingForm,
    isMobile,
    onUpdateResults,
    change,
    initialValues: { terms },
    initialValues,
    setType,
  } = props

  const { control, reset, watch, handleSubmit } = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
  })

  const loanType = watch('loanType')

  useEffect(() => {
    reset(initialValues)
  }, [initialValues, reset])

  const isPurchase = loanType === PURCHASE_TYPE

  const shouldShowLoanTypeFilter = isFeatureEnabled(LOAN_TYPE_FILTER)

  const formLoanType = initialValues?.type
  const formLoanTerm = initialValues?.term

  const [localType, setLocalType] = useState(formLoanType || {})
  const [loanTypeError, setLoanTypeError] = useState(false)

  const [loanTerm, setLoanTerm] = useState(formLoanTerm || {})
  const [loanTermError, setLoanTermError] = useState(false)

  useEffect(() => {
    setType(localType)
  }, [localType])

  useEffect(() => {
    Object.entries(localType).forEach(([key, value]) => {
      if (value !== formLoanType?.[key]) {
        change(`type.${key}`, value)
      }
    })
  }, [JSON.stringify(localType), JSON.stringify(formLoanType)])

  useEffect(() => {
    Object.entries(loanTerm).forEach(([key, value]) => {
      if (value !== formLoanTerm?.[key]) {
        change(`term.${key}`, value)
      }
    })
  }, [JSON.stringify(loanTerm), JSON.stringify(formLoanTerm)])

  return (
    <Testable testId="header-body">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.sortAndTerm}>
          <Testable testId="sortAndTerm">
            <span className={cx(styles.header, 'medium semiBold')}>Today’s mortgage</span>
            <Dropdown
              control={control}
              className={styles.verticalDropdown}
              name="loanType"
              id="loanType"
              label="Vertical"
            >
              {renderDropdownOptions(verticalOptions)}
            </Dropdown>
            <span className={cx(styles.header, 'medium semiBold')}>rates</span>
          </Testable>
        </div>
        <section className={cx(styles.formBody, { [styles.collapseBorder]: isOpen }, 'mt-16')}>
          <Collapse theme={{ collapse: 'collapseContainer' }} isOpened={isOpen}>
            {isMobile && sortingForm && <div className="mb-16">{sortingForm}</div>}
            <div className={styles.termAndTypeFilters}>
              <div className={styles.termContainer}>
                <div className={styles.loanTermInfo}>
                  <p className="disclaimer semiBold">Loan term </p>
                  <p className={styles.helpIcon}>
                    <HelpIconWithModal
                      modalName={LOAN_TERM_MODAL_NAME}
                      scrollToModal
                      isMobile={isMobile}
                      color="secondary500"
                      modalComponent={LoanTermModal}
                    />
                  </p>
                </div>
                <div className={styles.termCheckBoxes}>
                  <Testable testId="loan-type-term" className={styles.termCheckBoxesWrapper}>
                    <div className={styles.checkboxes}>
                      {terms.map((term, index) => (
                        <Checkbox
                          control={control}
                          id={`${term}-${index}-id`}
                          borderType="noBorder"
                          disableFullWidth
                          name={`term.${term}`}
                          label={termsHeaderLabelShortVersion[term]}
                          className={cx('mb-md-16', styles.checkboxWrapper)}
                          key={term}
                          inputProps={{
                            checked: loanTerm[term],
                            onChange: (e) => {
                              if (!e.target.checked) {
                                if (isOtherPropertiesFalsy(term, loanTerm)) {
                                  setLoanTermError(true)
                                  e.preventDefault()
                                  return
                                }
                              }
                              setLoanTermError(false)
                              setLoanTerm((prev) => ({ ...prev, [term]: e.target.checked }))
                            },
                          }}
                        />
                      ))}
                    </div>
                    {loanTermError && (
                      <p className="disclaimer error500">Please select at least one loan term</p>
                    )}
                  </Testable>
                </div>
              </div>
              {shouldShowLoanTypeFilter && (
                <div className={styles.typeContainer}>
                  <div className={styles.loanTypeInfo}>
                    <p className="disclaimer semiBold">Loan type </p>
                    <p className={styles.helpIcon}>
                      <HelpIconWithModal
                        modalName={LOAN_TYPE_MODAL_NAME}
                        scrollToModal
                        isMobile={isMobile}
                        color="neutral"
                        modalComponent={LoanTypeModal}
                      />
                    </p>
                  </div>
                  <div className={styles.typeCheckBoxes}>
                    <Testable testId="loan-type-term" className={styles.loanType}>
                      <div className={cx(styles.checkboxes, styles.loanTypeCheckbox)}>
                        {loanTypesList.map((type, index) => (
                          <Checkbox
                            control={control}
                            id={`${type}-${index}-id`}
                            borderType="noBorder"
                            disableFullWidth
                            name={`type.${type}`}
                            label={filterTypeList[type]}
                            className={cx('mb-md-16', styles.checkboxWrapper)}
                            key={type}
                            inputProps={{
                              checked: localType[type],
                              onChange: (e) => {
                                if (!e.target.checked) {
                                  if (!giveAdjacentValue(type, localType)) {
                                    e.preventDefault()
                                    setLoanTypeError(true)
                                    return
                                  }
                                }
                                setLoanTypeError(false)
                                setLocalType((prev) => ({ ...prev, [type]: e.target.checked }))
                              },
                            }}
                          />
                        ))}
                      </div>
                      {loanTypeError && (
                        <p className="disclaimer error500">Please select at least one loan type</p>
                      )}
                    </Testable>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.inputContainer}>
              <div className={cx(styles.inputWrapper)}>
                <Input
                  control={control}
                  name={isPurchase ? 'zipCode' : 'zipCodeRefi'}
                  id={isPurchase ? 'zipCode' : 'zipCodeRefi'}
                  label="Zip code"
                  placeholder="Zip code"
                  analyticsContexts={analyticsContexts}
                  numberFormat={(value) => value}
                  inputProps={{
                    onChange: handleChangeZip,
                  }}
                  controllerProps={{ rules: { validate: { validateZipWithPrefill } } }}
                />
              </div>
              <div className={cx(styles.inputWrapper)}>
                <Money
                  control={control}
                  name={isPurchase ? 'estimatedValue' : 'homeValue'}
                  id={isPurchase ? 'estimatedValue' : 'homeValue'}
                  label={isPurchase ? 'Purchase price' : 'Home value'}
                  placeholder={isPurchase ? 'Purchase price' : 'Home value'}
                />
              </div>
              <div className={cx(styles.inputWrapper)}>
                <Money
                  control={control}
                  name={isPurchase ? 'downPayment' : 'loanAmount'}
                  id={isPurchase ? 'downPayment' : 'loanAmount'}
                  label={isPurchase ? 'Down payment' : 'Mortgage balance'}
                  placeholder={isPurchase ? 'Down payment' : 'Mortgage balance'}
                />
              </div>
              <div className={cx(styles.inputWrapper)}>
                <Dropdown
                  control={control}
                  label="Credit score range"
                  name={isPurchase ? 'ficoType' : 'ficoTypeRefi'}
                  id={isPurchase ? 'ficoType' : 'ficoTypeRefi'}
                  icon={false}
                  analyticsOptions={{ fieldLabel: 'Credit score range' }}
                >
                  {renderDropdownOptions(ficoTypes)}
                </Dropdown>
              </div>
              <div className={cx(styles.inputWrapper)}>
                <Dropdown
                  control={control}
                  label="Property type"
                  name={isPurchase ? 'propertyType' : 'propertyTypeRefi'}
                  id={isPurchase ? 'propertyType' : 'propertyTypeRefi'}
                  icon={false}
                  analyticsOptions={{ fieldLabel: 'Property type' }}
                >
                  {renderDropdownOptions(propertyTypes)}
                </Dropdown>
              </div>
              <div className={cx(styles.inputWrapper)}>
                <Dropdown
                  control={control}
                  label="Property usage"
                  name={isPurchase ? 'occupancyType' : 'occupancyTypeRefi'}
                  id={isPurchase ? 'occupancyType' : 'occupancyTypeRefi'}
                  icon={false}
                  analyticsOptions={{ fieldLabel: 'Property usage' }}
                >
                  {renderDropdownOptions(occupancyTypes)}
                </Dropdown>
              </div>
              {!isPurchase && (
                <div className={cx(styles.inputWrapper)}>
                  <Money
                    control={control}
                    name="cashOut"
                    id="cashOut"
                    label="Cash out amount"
                    placeholder="Cash out amount"
                  />
                </div>
              )}
            </div>

            <div className={styles.dismissAndUpdate}>
              {isOpen && <div className={styles.editBtn}>{editButton}</div>}
              <CtaButton
                data-test-id="submitBTN"
                variant="outlined"
                className={styles.button}
                analyticsContexts={analyticsContexts}
                loading={loadingProducts}
                showDisabled={invalid || isCashOutError || isValidationsError}
                onClick={() => {
                  onUpdateResults(false)
                  setLoanTypeError(false)
                  setLoanTermError(false)
                }}
              >
                Update Results
              </CtaButton>
            </div>
          </Collapse>
        </section>
        {!isOpen && (
          <div className={cx(styles.filterWrapper)}>
            <div className={styles.filterPart}>
              <p className="bodySmall semiBold">{isMobile ? 'Filter & Sort' : 'Filters'}</p>
              <p className={cx(styles.filterText, 'disclaimer neutral500')}>{filterText}</p>
            </div>
            <div className={styles.editBtn}>{editButton}</div>
          </div>
        )}
      </form>
    </Testable>
  )
}

FilterFormCombinedNoHOC.propTypes = {
  /* eslint-disable-next-line */
  isPurchase: PropTypes.bool.isRequired,
  invalid: PropTypes.bool,
  analyticsContexts: PropTypes.array,
  loadingProducts: PropTypes.bool.isRequired,
  isCashOutError: PropTypes.bool.isRequired,
  isValidationsError: PropTypes.bool.isRequired,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  sortingForm: PropTypes.element,
  editButton: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  filterText: PropTypes.string,
  isMobile: PropTypes.bool.isRequired,
  onUpdateResults: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  setType: PropTypes.func.isRequired,
}

export { FilterFormCombinedNoHOC }

const FilterFormCombined = compose(
  withSnowplowContexts,
  // withStyles(defaultStyles, { fox1, insider, nypost }),
  reduxForm({
    keepDirtyOnReinitialize: false,
    enableReinitialize: true,
    destroyOnUnmount: false,
  }),
)(FilterFormCombinedNoHOC)

export default FilterFormCombined
